import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

const Clause: React.FC<{ number: string; children: React.ReactNode }> = ({ number, children }) => (
  <Box sx={{ display: 'flex', mb: 1.5 }}>
    <Box sx={{ width: 48, flexShrink: 0, fontWeight: 600, fontSize: '0.875rem' }}>{number}</Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const SubClause: React.FC<{ marker: string; children: React.ReactNode }> = ({ marker, children }) => (
  <Box sx={{ display: 'flex', mb: 1, pl: 2 }}>
    <Box sx={{ width: 32, flexShrink: 0, fontSize: '0.875rem' }}>{marker}</Box>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const Definition: React.FC<{ term: string; children: React.ReactNode }> = ({ term, children }) => (
  <Typography variant="body2" paragraph>
    <strong>{term}</strong>
    {': '}
    {children}
  </Typography>
);

const Section: React.FC<{ number: string; title: string; children: React.ReactNode }> = ({
  number,
  title,
  children,
}) => (
  <Box sx={{ mt: 3, mb: 2 }}>
    <Typography variant="h6" gutterBottom>
      {number}
      {'. '}
      {title}
    </Typography>
    {children}
  </Box>
);

const Para: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" component="div">
    {children}
  </Typography>
);

interface TermsAndConditionsContentProps {
  showAcceptConfirmation?: boolean;
}

const TermsAndConditionsContent: React.FC<TermsAndConditionsContentProps> = ({
  showAcceptConfirmation = true,
}) => (
  <Box>
    <Typography variant="h5" sx={{ fontWeight: 600 }} gutterBottom>
      Mine Smart Solutions Ltd
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
      Standard Terms and Conditions: Software as a Service
    </Typography>

    <Section number="1" title="Definitions and Interpretation">
      <Clause number="1.1">
        <Para>The definitions and rules of interpretation in this clause apply in this agreement.</Para>
        <Box sx={{ mt: 1 }}>
          <Definition term="Authorised Users">
            those employees, agents and independent contractors of the Client who are authorised by the
            Client to use the Services and the Documentation.
          </Definition>
          <Definition term="Business Day">
            a day other than a Saturday, Sunday or public holiday in England when banks in London are open
            for business.
          </Definition>
          <Definition term="Client">the person or company to whom the Supplier supplies the Services.</Definition>
          <Definition term="Client Data">
            the data inputted by the Client, Authorised Users, or the Supplier on the Client&apos;s behalf
            for the purpose of using the Services or facilitating the Client&apos;s use of the Services.
          </Definition>
          <Definition term="Documentation">
            all information and in documents in whatever form made available to the Client by the Supplier
            whether online via a web site or to the Client from time to time or via email (or in such other
            format as the Supplier may at its sole discretion elect) which set out a description of or
            otherwise relates to the provision of the Services including but not limited to the user
            instructions for the Services.
          </Definition>
          <Definition term="Effective Date">the date of this agreement.</Definition>
          <Definition term="Heightened Cybersecurity Requirements">
            any laws, regulations, codes, guidance (from regulatory and advisory bodies whether mandatory
            or not), international and national standards, industry schemes and sanctions, which are
            applicable to either the Client or an Authorised User (but not the Supplier) relating to
            security of network and information systems and security breach and incident reporting
            requirements, which may include the cybersecurity Directive ((EU) 2016/1148), Commission
            Implementing Regulation ((EU) 2018/151), the Network and Information systems Regulations 2018
            (SI 506/2018), all as amended or updated from time to time.
          </Definition>
          <Definition term="Initial Subscription Term">
            the initial term of this agreement as set out in Schedule 2.
          </Definition>
          <Definition term="Mandatory Policies">
            the Supplier&apos;s business policies and codes listed in Schedule 3, as amended by notification
            to the Client from time to time.
          </Definition>
          <Definition term="Non-Authorised User">
            any user of the Services not explicitly authorised in writing and under the terms of this
            agreement.
          </Definition>
          <Definition term="Normal Business Hours">9.00 am to 5.00 pm local UK time, each Business Day.</Definition>
          <Definition term="Renewal Period">the period described in clause 14.1.</Definition>
          <Definition term="Services">
            the subscription and any other services provided by the Supplier to the Client under this
            agreement in any form whether via a web address or otherwise as more particularly described in
            in Schedule 4 and in the Documentation.
          </Definition>
          <Definition term="Software">
            the online software applications provided by the Supplier as part of the Services.
          </Definition>
          <Definition term="Subscription Fees">
            the subscription fees payable by the Client to the Supplier for the User Subscriptions, as set
            out in paragraph 1 of Schedule 1.
          </Definition>
          <Definition term="Subscription Term">
            has the meaning given in clause 14.1 (being the Initial Subscription Term together with any
            subsequent Renewal Periods).
          </Definition>
          <Definition term="Supplier">
            Mine Smart Solutions Ltd incorporated and registered in England and Wales with company number
            14968191 whose registered office is at Wheal Jane Earth Science Park, Baldhu, Truro, TR3 6EE UK.
          </Definition>
          <Definition term="Support Services">
            the support services offered by the Supplier as set out in Schedule 5 or as otherwise agreed in
            writing between the parties.
          </Definition>
          <Definition term="User Subscriptions">
            the user subscriptions purchased by the Client pursuant to clause 9.1 which entitle Authorised
            Users to access and use the Services and the Documentation in accordance with this agreement.
          </Definition>
          <Definition term="Virus">
            any thing or device (including any software, code, file or programme) which may: prevent,
            impair or otherwise adversely affect the operation of any computer software, hardware or
            network, any telecommunications service, equipment or network or any other service or device;
            prevent, impair or otherwise adversely affect access to or the operation of any programme or
            data, including the reliability of any programme or data (whether by re-arranging, altering or
            erasing the programme or data in whole or part or otherwise); or adversely affect the user
            experience, including worms, trojan horses, viruses and other similar things or devices.
          </Definition>
          <Definition term="Vulnerability">
            a weakness in the computational logic (for example, code) found in software and hardware
            components that when exploited, results in a negative impact to the confidentiality, integrity,
            or availability, and the term
            {' '}
            <strong>Vulnerabilities</strong>
            {' '}
            shall be interpreted accordingly.
          </Definition>
        </Box>
      </Clause>
      <Clause number="1.2">
        <Para>
          The Schedules form part of this agreement and shall have effect as if set out in full in the body
          of this agreement. Any reference to this agreement includes the Schedules.
        </Para>
      </Clause>
    </Section>

    <Section number="2" title="Commencement">
      <Clause number="2.1">
        <Para>
          This agreement takes effect from the date when the Supplier commences performance of the Services
          or such other date as may be agreed between the parties, regardless of the date of this agreement.
        </Para>
      </Clause>
    </Section>

    <Section number="3" title="User subscriptions">
      <Clause number="3.1">
        <Para>
          Subject to the Client complying with its obligations under this agreement the Supplier hereby
          grants to the Client a non-exclusive, non-transferable right and licence, without the right to
          grant sublicences, to permit the Authorised Users to use the Services and the Documentation
          during the Subscription Term solely for the Client&apos;s internal business operations.
        </Para>
      </Clause>
      <Clause number="3.2">
        <Para>
          Under this agreement the maximum number of Authorised Users shall be as set out in Schedule 1.
        </Para>
      </Clause>
      <Clause number="3.3">
        <Para>
          Should the Client require an increased maximum number of Authorised Users per User Subscription
          such an increase shall be permitted at the exclusive discretion of the Supplier. The Supplier
          reserves the right to increase the Subscription Fees proportionately in the event of an increase
          in the maximum number of Authorised Users.
        </Para>
      </Clause>
      <Clause number="3.4">
        <Para>Use by Non-Authorised Users is not permitted under this agreement.</Para>
        <SubClause marker="(a)">
          <Para>
            The Client undertakes that it will not allow or suffer any User Subscription to be used by more
            than one individual Authorised User unless it has been reassigned in its entirety to another
            individual Authorised User, in which case the prior Authorised User shall no longer have any
            right to access or use the Services and/or Documentation.
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            each Authorised User shall keep a secure password for their use of the Services and
            Documentation, that such password shall be changed no less frequently than every three months
            (or such other period as the Supplier may instruct from time to time) and that each Authorised
            User shall keep their password confidential.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="3.5">
        <Para>
          The Client is responsible for its use of the Service including the conduct of Authorised Users
          and any Non-Authorised Users and must ensure that all use is in accordance with the agreement.
          The Client shall notify the Supplier immediately of any breaches of this agreement by any
          Authorised Users or Non-Authorised Users.
        </Para>
      </Clause>
      <Clause number="3.6">
        <Para>
          The Client shall not access, store, distribute or transmit any Viruses, or any material during
          the course of its use of the Services that:
        </Para>
        <SubClause marker="(a)">
          <Para>
            is or may be unlawful, harmful, threatening, defamatory, obscene, infringing, harassing or
            racially or ethnically offensive;
          </Para>
        </SubClause>
        <SubClause marker="(b)"><Para>facilitates or may facilitate illegal activity;</Para></SubClause>
        <SubClause marker="(c)"><Para>depicts or may be deemed to depict sexually explicit images;</Para></SubClause>
        <SubClause marker="(d)"><Para>promotes or may promote unlawful violence;</Para></SubClause>
        <SubClause marker="(e)">
          <Para>
            is discriminatory or may be deemed to be discriminatory based on race, gender, colour,
            religious belief, sexual orientation, disability; or
          </Para>
        </SubClause>
        <SubClause marker="(f)">
          <Para>
            is otherwise or the Supplier reasonably believes is illegal or causes damage or injury to any
            person or property;
          </Para>
        </SubClause>
        <Para>
          and the Supplier reserves the right, without liability or prejudice to its other rights to the
          Client, to disable the Client&apos;s access to any material that breaches the provisions of this
          clause.
        </Para>
      </Clause>
      <Clause number="3.7">
        <Para>The Client shall not:</Para>
        <SubClause marker="(a)">
          <Para>
            except as may be allowed by any applicable law which is incapable of exclusion by agreement
            between the parties and except to the extent expressly permitted under this agreement:
          </Para>
          <SubClause marker="(i)">
            <Para>
              attempt to copy, modify, duplicate, create derivative works from, frame, mirror, republish,
              download, display, transmit, or distribute all or any portion of the Software and/or
              Documentation (as applicable) in any form or media or by any means; or
            </Para>
          </SubClause>
          <SubClause marker="(ii)">
            <Para>
              attempt to de-compile, reverse compile, disassemble, reverse engineer or otherwise reduce to
              human-perceivable form all or any part of the Software; or
            </Para>
          </SubClause>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            access all or any part of the Services and Documentation in order to build a product or service
            which competes with the Services and/or the Documentation; or
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>use the Services and/or Documentation to provide services to third parties; or</Para>
        </SubClause>
        <SubClause marker="(d)">
          <Para>
            subject to clause 22.1, license, sell, rent, lease, transfer, assign, distribute, display,
            disclose, or otherwise commercially exploit, or otherwise make the Services and/or
            Documentation available to any third party except the Authorised Users, or
          </Para>
        </SubClause>
        <SubClause marker="(e)">
          <Para>
            attempt to obtain, or assist third parties in obtaining, access to the Services and/or
            Documentation, other than as provided under this clause 3; or
          </Para>
        </SubClause>
        <SubClause marker="(f)">
          <Para>
            introduce or permit the introduction of, any Virus or Vulnerability into the Supplier&apos;s
            network and information systems.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="3.8">
        <Para>
          The Client shall use all reasonable endeavours to prevent any unauthorised access to, or use of,
          the Services and/or the Documentation and, in the event of any such unauthorised access or use,
          promptly notify the Supplier.
        </Para>
      </Clause>
      <Clause number="3.9">
        <Para>
          The rights provided under this clause 3 are granted to the Client only, and shall not be
          considered granted to any subsidiary or holding company of the Client.
        </Para>
      </Clause>
    </Section>

    <Section number="4" title="Services">
      <Clause number="4.1">
        <Para>
          The Supplier shall, during the Subscription Term, provide the Services and make available the
          Documentation to the Client on and subject to the terms of this agreement.
        </Para>
      </Clause>
      <Clause number="4.2">
        <Para>
          The Supplier shall use commercially reasonable endeavours to make the Services available 24 hours
          a day, seven days a week, except for:
        </Para>
        <SubClause marker="(a)">
          <Para>
            planned maintenance carried out during such times and periods as may be notified to the Client
            from time to time with a minimum of 24 hours&apos; notice and
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            unscheduled maintenance performed outside Normal Business Hours, provided that the Supplier
            has used reasonable endeavours to give the Client at least six Normal Business Hours&apos;
            notice in advance.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="4.3">
        <Para>
          The Supplier will, as part of the Services and at no additional cost to the Client, provide the
          Client with the Support Services during Normal Business Hours. The Supplier may amend the
          Support Services in its sole and absolute discretion from time to time.
        </Para>
      </Clause>
      <Clause number="4.4">
        <Para>
          The Client may purchase such enhanced support services as the Supplier may offer from time to
          time (Enhanced Support Services) separately as agreed between the parties.
        </Para>
      </Clause>
      <Clause number="4.5">
        <Para>The Supplier reserves the right to amend the specification of the Services:</Para>
        <SubClause marker="(a)"><Para>if required by any applicable statutory or regulatory requirement; or</Para></SubClause>
        <SubClause marker="(b)"><Para>as part of the development of the Services; or</Para></SubClause>
        <SubClause marker="(c)">
          <Para>if the amendment does not materially affect the nature or quality of the Services</Para>
        </SubClause>
        <Para>
          and the Supplier will use reasonable endeavours to notify the Client in advance of any such
          amendment.
        </Para>
      </Clause>
    </Section>

    <Section number="5" title="Data protection">
      <Clause number="5.1">
        <Para>The Supplier will process any personal data provided to it by the Client to:</Para>
        <SubClause marker="(a)"><Para>provide the Services;</Para></SubClause>
        <SubClause marker="(b)"><Para>process the payment for the Services; and</Para></SubClause>
        <SubClause marker="(c)">
          <Para>
            inform the Client about similar products or services that the Supplier or any member of the
            group of companies to which the Supplier belongs may provide and the Client may stop receiving
            such information at any time by contacting the Supplier.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="5.2">
        <Para>
          The Supplier will process personal information in accordance with its Privacy Notice the terms of
          which as amended from time to time are incorporated into this contract and are available to the
          Client at
          {' '}
          <Link href="https://minesmart.io/privacy-policy/" target="_blank" rel="noopener noreferrer">
            https://minesmart.io/privacy-policy/
          </Link>
          .
        </Para>
      </Clause>
    </Section>

    <Section number="6" title="Third party providers">
      <Para>
        The Client acknowledges that the Services may enable or assist it to access the website content of,
        correspond with, and purchase products and services from, third parties via third-party websites
        and that it does so solely at its own risk. The Supplier makes no representation, warranty or
        commitment and shall have no liability or obligation whatsoever in relation to the content or use
        of, or correspondence with, any such third-party website, or any transactions completed, and any
        contract entered into by the Client, with any such third party. Any contract entered into and any
        transaction completed via any third-party website is between the Client and the relevant third
        party, and not the Supplier. The Supplier recommends that the Client refers to the third
        party&apos;s website terms and conditions and privacy policy prior to using the relevant
        third-party website. The Supplier does not endorse or approve any third-party website nor the
        content of any of the third-party website made available via the Services.
      </Para>
    </Section>

    <Section number="7" title="Supplier's obligations">
      <Clause number="7.1">
        <Para>
          The Supplier undertakes that the Services will be performed substantially in accordance with the
          Documentation and with reasonable skill and care.
        </Para>
      </Clause>
      <Clause number="7.2">
        <Para>
          The undertaking at clause 7.1 shall not apply to the extent of any non-conformance which is
          caused by use of the Services contrary to the Supplier&apos;s instructions, or modification or
          alteration of the Services by any party other than the Supplier or the Supplier&apos;s duly
          authorised contractors or agents. If the Services do not conform with the foregoing undertaking,
          Supplier will, at its expense, use all reasonable commercial endeavours to correct any such
          non-conformance promptly, or provide the Client with an alternative means of accomplishing the
          desired performance. Such correction or substitution constitutes the Client&apos;s sole and
          exclusive remedy for any breach of the undertaking set out in clause 7.1.
        </Para>
      </Clause>
      <Clause number="7.3">
        <Para>The Supplier:</Para>
        <SubClause marker="(a)">
          <Para>does not warrant that:</Para>
          <SubClause marker="(i)">
            <Para>the Client&apos;s use of the Services will be uninterrupted or error-free; or</Para>
          </SubClause>
          <SubClause marker="(ii)">
            <Para>
              that the Services, Documentation and/or the information obtained by the Client through the
              Services will meet the Client&apos;s requirements; or
            </Para>
          </SubClause>
          <SubClause marker="(iii)">
            <Para>the Software or the Services will be free from Vulnerabilities or Viruses; or</Para>
          </SubClause>
          <SubClause marker="(iv)">
            <Para>
              the Software, Documentation or Services will comply with any Heightened Cybersecurity
              Requirements.
            </Para>
          </SubClause>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            is not responsible for any delays, delivery failures, or any other loss or damage resulting
            from the transfer of data over communications networks and facilities, including the internet,
            and the Client acknowledges that the Services and Documentation may be subject to limitations,
            delays and other problems inherent in the use of such communications facilities.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="7.4">
        <Para>
          This agreement shall not prevent the Supplier from entering into similar agreements with third
          parties, or from independently developing, using, selling or licensing documentation, products
          and/or services which are similar to those provided under this agreement.
        </Para>
      </Clause>
      <Clause number="7.5">
        <Para>
          The Supplier warrants that it has and will maintain all necessary licences, consents, and
          permissions necessary for the performance of its obligations under this agreement.
        </Para>
      </Clause>
      <Clause number="7.6">
        <Para>
          The Supplier shall be entitled to rely on any and all information provided to it by or on behalf
          of the Client save only where such information is manifestly inaccurate.
        </Para>
      </Clause>
    </Section>

    <Section number="8" title="Client's obligations">
      <Clause number="8.1">
        <Para>The Client shall:</Para>
        <SubClause marker="(a)">
          <Para>provide the Supplier with:</Para>
          <SubClause marker="(i)"><Para>all necessary co-operation in relation to this agreement; and</Para></SubClause>
          <SubClause marker="(ii)">
            <Para>
              all such information and materials as the Supplier may reasonably require together with any
              additional information which may be pertinent whether requested by the Supplier or otherwise
              and ensure that such information is complete, accurate and up to date at all times and in all
              material respects;
            </Para>
          </SubClause>
          <Para>
            in order to provide the Services, including but not limited to Client Data, security access
            information and configuration services;
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            without affecting its other obligations under this agreement, comply with all applicable laws
            and regulations with respect to its activities under this agreement;
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            carry out all other Client responsibilities set out in this agreement in a timely and efficient
            manner. In the event of any delays in the Client&apos;s provision of such assistance as agreed
            by the parties, the Supplier may adjust any agreed timetable or delivery schedule as reasonably
            necessary;
          </Para>
        </SubClause>
        <SubClause marker="(d)">
          <Para>
            ensure that the Authorised Users use the Services and the Documentation in accordance with the
            terms and conditions of this agreement and shall be responsible for any Authorised User&apos;s
            breach of this agreement;
          </Para>
        </SubClause>
        <SubClause marker="(e)">
          <Para>
            obtain and shall maintain all necessary licences, consents, and permissions necessary for the
            Supplier, its contractors and agents to perform their obligations under this agreement,
            including without limitation the Services;
          </Para>
        </SubClause>
        <SubClause marker="(f)">
          <Para>
            ensure that its network and systems comply with the relevant specifications provided by the
            Supplier from time to time; and
          </Para>
        </SubClause>
        <SubClause marker="(g)">
          <Para>
            be, to the extent permitted by law and except as otherwise expressly provided in this
            agreement, solely responsible for procuring, maintaining and securing its network connections
            and telecommunications links from its systems to the Supplier&apos;s data centres, and all
            problems, conditions, delays, delivery failures and all other loss or damage arising from or
            relating to the Client&apos;s network connections or telecommunications links or caused by the
            internet.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="8.2">
        <Para>
          The Client shall own all right, title and interest in and to all of the Client Data that is not
          personal data and shall have sole responsibility for the legality, reliability, integrity,
          accuracy and quality of all such Client Data.
        </Para>
      </Clause>
    </Section>

    <Section number="9" title="Charges and payment">
      <Clause number="9.1">
        <Para>
          The Client shall pay the Subscription Fees to the Supplier for the User Subscriptions in
          accordance with this clause 9 and Schedule 1 and any Enhanced Support fees in accordance with
          clause 4.3 and Schedule 1.
        </Para>
      </Clause>
      <Clause number="9.2">
        <Para>
          The Client shall reimburse the Supplier in full for any additional expenses or disbursements
          properly incurred in accordance with this agreement at the rates and terms set out in the
          agreement.
        </Para>
      </Clause>
      <Clause number="9.3">
        <Para>
          The Client shall pay the Supplier any VAT or other taxes properly chargeable on the Services and
          any amount expressed as payable to the Supplier under this agreement is exclusive of VAT and any
          other sales taxes unless stated otherwise.
        </Para>
      </Clause>
      <Clause number="9.4">
        <Para>
          The Client shall be liable for and pay and/or reimburse to the Supplier as appropriate any
          deduction or assessment of any other taxes and similar assessments imposed on the Supplier as a
          result of the provision of the Services by any national, federal, provincial, municipal or other
          statutory taxing authority in addition to the Subscription Fees and any other cost or
          disbursements. Notwithstanding the generality of the provisions of this clause 9 the Client
          shall make all payments without withholding or deduction of or in respect of any tax unless
          required by law. If any such withholding or deduction is required the Client shall, when making
          the payment to which the withholding or deduction relates pay to the Supplier such additional
          amount as will ensure that the Supplier receives the same total amount that would have been
          received if no such withholding or deduction had been required.
        </Para>
      </Clause>
      <Clause number="9.5">
        <Para>
          The Supplier may increase the Subscription Fees annually with effect from each anniversary of
          the date of this agreement. The Supplier will use reasonable endeavours to notify the Client
          not less than 2 calendar months in advance of the revised Subscription Fees.
        </Para>
      </Clause>
      <Clause number="9.6"><Para>The Subscription Fees shall be due and payable annually in advance.</Para></Clause>
      <Clause number="9.7">
        <Para>The Supplier shall submit to the Client an invoice:</Para>
        <SubClause marker="(a)">
          <Para>
            for each instalment of the Subscription Fees payable 30 days before the commencement of the
            Services; and
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>for any other sums payable monthly or as otherwise agreed between the parties.</Para>
        </SubClause>
      </Clause>
      <Clause number="9.8"><Para>Payment shall be due on the date the Client receives each invoice.</Para></Clause>
      <Clause number="9.9">
        <Para>The final date for payment shall be twenty-eight days after the date on which payment becomes due.</Para>
      </Clause>
      <Clause number="9.10">
        <Para>
          Invoices unpaid within twenty-eight days of the invoice date shall be subject to interest at the
          statutory rate.
        </Para>
      </Clause>
      <Clause number="9.11">
        <Para>
          If payment of any invoice is materially late, or the total sum outstanding is a material amount
          of the total Subscription Fees the Supplier shall be entitled to suspend provision of the
          Services until all such sums are paid in full unless legitimately disputed.
        </Para>
      </Clause>
      <Clause number="9.12">
        <Para>
          All sums payable to the Supplier shall be paid in full without any set-off, counterclaim,
          deduction or withholding (other than any deduction or withholding as required by law).
        </Para>
      </Clause>
      <Clause number="9.13">
        <Para>All amounts and fees stated or referred to in this agreement:</Para>
        <SubClause marker="(a)"><Para>shall be payable in pounds sterling;</Para></SubClause>
        <SubClause marker="(b)"><Para>are, subject to clause 13.3, non-cancellable and non-refundable.</Para></SubClause>
      </Clause>
    </Section>

    <Section number="10" title="Proprietary rights">
      <Clause number="10.1">
        <Para>
          The Client acknowledges and agrees that the Supplier and/or its licensors own all intellectual
          property rights in the Services and the Documentation. Except as expressly stated herein, this
          agreement does not grant the Client any rights to, under or in, any patents, copyright, database
          right, trade secrets, trade names, trade marks (whether registered or unregistered), or any
          other rights or licences in respect of the Services or the Documentation.
        </Para>
      </Clause>
      <Clause number="10.2">
        <Para>
          The Supplier confirms that it has all the rights in relation to the Services and the
          Documentation that are necessary to grant all the rights it purports to grant under, and in
          accordance with, the terms of this agreement.
        </Para>
      </Clause>
    </Section>

    <Section number="11" title="Confidentiality">
      <Clause number="11.1">
        <Para>
          Each party undertakes that it shall not at any time during this agreement and for a period of
          two years after termination or expiry of this agreement disclose to any person any confidential
          information concerning the business, assets, affairs, customers, clients, suppliers or plans of
          the other party or any member of the group of companies to which the other party belongs except
          as expressly permitted in this agreement.
        </Para>
      </Clause>
      <Clause number="11.2">
        <Para>Each party may disclose the other party&apos;s confidential information:</Para>
        <SubClause marker="(a)">
          <Para>
            to its employees, officers, representatives, contractors, subcontractors or advisers who need
            to know such information for the purposes of exercising the party&apos;s rights or carrying
            out its obligations under or in connection with this agreement. Each party shall ensure that
            its employees, officers, representatives, contractors, subcontractors or advisers to whom it
            discloses the other party&apos;s confidential information comply with this Clause 11; and
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            as may be required by law, a court of competent jurisdiction or any relevant governmental or
            regulatory authority.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="11.3">
        <Para>In performing its obligations under this agreement the Client shall comply with the Mandatory Policies.</Para>
      </Clause>
    </Section>

    <Section number="12" title="Indemnity">
      <Clause number="12.1">
        <Para>
          The Client shall defend, indemnify and hold harmless the Supplier against claims, actions,
          proceedings, losses, damages, expenses and costs (including without limitation court costs and
          reasonable legal fees) arising out of or in connection with the Client&apos;s use of the Services
          and/or Documentation, provided that:
        </Para>
        <SubClause marker="(a)"><Para>the Client is given prompt notice of any such claim;</Para></SubClause>
        <SubClause marker="(b)">
          <Para>
            the Supplier provides reasonable co-operation to the Client in the defence and settlement of
            such claim, at the Client&apos;s expense; and
          </Para>
        </SubClause>
        <SubClause marker="(c)"><Para>the Client is given sole authority to defend or settle the claim.</Para></SubClause>
      </Clause>
      <Clause number="12.2">
        <Para>
          The Supplier shall defend the Client, its officers, directors and employees against any claim
          that the Client&apos;s use of the Services or Documentation in accordance with this agreement
          infringes any patent effective as of the Effective Date, copyright, trade mark, database right
          or right of confidentiality to the extent that such action or claim arises out of a breach by
          the Supplier of its obligations under this agreement, and shall indemnify the Client for any
          amounts awarded against the Client in judgment or settlement of such claims, provided that:
        </Para>
        <SubClause marker="(a)"><Para>the Supplier is given prompt notice of any such claim;</Para></SubClause>
        <SubClause marker="(b)">
          <Para>
            the Client does not make any admission, or otherwise attempt to compromise or settle the claim
            and provides reasonable co-operation to the Supplier in the defence and settlement of such
            claim, at the Supplier&apos;s expense; and
          </Para>
        </SubClause>
        <SubClause marker="(c)"><Para>the Supplier is given sole authority to defend or settle the claim.</Para></SubClause>
      </Clause>
      <Clause number="12.3">
        <Para>
          In the defence or settlement of any claim, the Supplier may procure the right for the Client to
          continue using the Services, replace or modify the Services so that they become non-infringing
          or, if such remedies are not reasonably available, terminate this agreement on two Business
          Days&apos; notice to the Client without any additional liability or obligation to pay liquidated
          damages or other additional costs to the Client.
        </Para>
      </Clause>
      <Clause number="12.4">
        <Para>
          In no event shall the Supplier, its employees, agents and sub-contractors be liable to the
          Client to the extent that the alleged infringement is based on:
        </Para>
        <SubClause marker="(a)">
          <Para>a modification of the Services or Documentation by anyone other than the Supplier; or</Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            the Client&apos;s use of the Services or Documentation in a manner contrary to the
            instructions given to the Client by the Supplier; or
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            the Client&apos;s use of the Services or Documentation after notice of the alleged or actual
            infringement from the Supplier or any appropriate authority.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="12.5">
        <Para>
          The foregoing and clause 13.3 states the Client&apos;s sole and exclusive rights and remedies,
          and the Supplier&apos;s (including the Supplier&apos;s employees&apos;, agents&apos; and
          sub-contractors&apos;) entire obligations and liability, for infringement of any patent,
          copyright, trade mark, database right or right of confidentiality.
        </Para>
      </Clause>
    </Section>

    <Section number="13" title="Limitation of liability">
      <Clause number="13.1">
        <Para>Except as expressly and specifically provided in this agreement:</Para>
        <SubClause marker="(a)">
          <Para>
            the Client assumes sole responsibility for results obtained from the use of the Services and
            the Documentation by the Client, and for conclusions drawn from such use. The Supplier shall
            have no liability for any damage caused by errors or omissions in any information, instructions
            or scripts provided to the Supplier by the Client in connection with the Services, or any
            actions taken by the Supplier at the Client&apos;s direction;
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            all warranties, representations, conditions and all other terms of any kind whatsoever implied
            by statute or common law are, to the fullest extent permitted by applicable law, excluded from
            this agreement; and
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>the Services and the Documentation are provided to the Client on an &quot;as is&quot; basis.</Para>
        </SubClause>
      </Clause>
      <Clause number="13.2">
        <Para>Subject to clause 13.1 and clause 13.4 the Supplier shall not be responsible or liable for:</Para>
        <SubClause marker="(a)">
          <Para>
            any claims arising from the errors, omissions or negligent acts of the Client, it&apos;s other
            consultants, contractors or any other persons;
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            any specification, design or any other information provided by the Client or any third party
            any repercussions thereof, or the failure of the Client or any third party to provide such
            specification, design or other information;
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            the design of or defects in any equipment provided by or on behalf of, or operated by or on
            behalf of the Client or any purpose;
          </Para>
        </SubClause>
        <SubClause marker="(d)">
          <Para>
            decisions made by the Client without the advice of the Supplier or contrary to inconsistent
            with the advice of the Supplier;
          </Para>
        </SubClause>
        <SubClause marker="(e)">
          <Para>
            any special, indirect or consequential loss, injury or damages suffered by the Client
            including but not limited to loss of use, or earnings, business interruption, loss of business
            opportunities, loss of expected savings, or other commercial or economic costs or losses of
            any kind even if the Supplier has been advised of the possibility of such damages;
          </Para>
        </SubClause>
        <SubClause marker="(f)">
          <Para>
            the unauthorised distribution of any confidential document prepared by or on behalf of the
            Supplier for the exclusive use of the Client or any other claim arising as a result of or in
            connection with such unauthorised distribution;
          </Para>
        </SubClause>
        <Para>
          each whether arising under or in connection with this agreement and whether based in contract,
          tort, debt, breach of statutory duty or otherwise.
        </Para>
      </Clause>
      <Clause number="13.3">
        <Para>
          Without affecting any other limitation in this agreement and otherwise notwithstanding any other
          provisions of this agreement, the Supplier&apos;s liability under or in connection with this
          agreement whether in contract, tort, debt, breach of statutory duty or otherwise shall be
          limited to the lesser of:
        </Para>
        <SubClause marker="(a)">
          <Para>
            the amount (if any) that the Supplier shall be entitled to be indemnified under its
            professional indemnity insurance; or
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>twice the Supplier&apos;s total fee for carrying out the Services in any twelve-month period;</Para>
        </SubClause>
      </Clause>
      <Clause number="13.4">
        <Para>
          The limitations set out in this clause shall apply however that liability arises except this
          clause shall not exclude or limit the Supplier&apos;s liability for:
        </Para>
        <SubClause marker="(a)"><Para>death or personal injury caused by the Supplier&apos;s negligence; or</Para></SubClause>
        <SubClause marker="(b)"><Para>fraud or fraudulent misrepresentation.</Para></SubClause>
      </Clause>
    </Section>

    <Section number="14" title="Term and termination">
      <Clause number="14.1">
        <Para>
          This agreement shall, unless otherwise terminated as provided in this clause 14, commence on the
          Effective Date and shall continue for the Initial Subscription Term and, thereafter, this
          agreement shall be automatically renewed for successive periods of 12 months (each a
          {' '}
          <strong>Renewal Period</strong>
          ), unless:
        </Para>
        <SubClause marker="(a)">
          <Para>
            either party notifies the other party of termination, in writing, at least 60 days before the
            end of the Initial Subscription Term or any Renewal Period, in which case this agreement shall
            terminate upon the expiry of the applicable Initial Subscription Term or Renewal Period; or
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>otherwise terminated in accordance with the provisions of this agreement;</Para>
        </SubClause>
        <Para>
          and the Initial Subscription Term together with any subsequent Renewal Periods shall constitute
          the
          {' '}
          <strong>Subscription Term</strong>
          .
        </Para>
      </Clause>
      <Clause number="14.2">
        <Para>
          Without affecting any other right or remedy available to it, either party may terminate this
          agreement with immediate effect by giving written notice to the other party if:
        </Para>
        <SubClause marker="(a)">
          <Para>
            the other party fails to pay any amount due under this agreement on the due date for payment
            and remains in default not less than 30 days after being notified in writing to make such
            payment;
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            the other party commits a material breach of any other term of this agreement and (if such
            breach is remediable) fails to remedy that breach within a period of 30 days after being
            notified in writing to do so;
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            the other party takes any other action which reasonably leads the innocent party to believe
            that the other party&apos;s conduct is inconsistent with each having the intention or ability
            to give effect to the terms of this agreement, including but not limited to the Client&apos;s
            financial position having deteriorated (or appearing on reasonable evidence to have
            deteriorated) to such an extent that in the Consultant&apos;s opinion the Client&apos;s
            capability to adequately fulfil its financial obligations under this agreement has been placed
            in jeopardy.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="14.3">
        <Para>On termination of this agreement for any reason:</Para>
        <SubClause marker="(a)">
          <Para>
            all licences granted under this agreement shall immediately terminate and the Client shall
            immediately cease all use of the Services and/or the Documentation;
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            each party shall return and make no further use of any equipment, property, Documentation and
            other items (and all copies of them) belonging to the other party;
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            the Supplier may destroy or otherwise dispose of any of the Client Data in its possession
            unless the Supplier receives, no later than ten days after the effective date of the
            termination of this agreement, a written request for the delivery to the Client of the then
            most recent back-up of the Client Data. The Supplier shall use reasonable commercial
            endeavours to deliver the back-up to the Client within 30 days of its receipt of such a
            written request, provided that the Client has, at that time, paid all fees and charges
            outstanding at and resulting from termination (whether or not due at the date of termination).
            The Client shall pay all reasonable expenses incurred by the Supplier in returning or
            disposing of Client Data; and
          </Para>
        </SubClause>
        <SubClause marker="(d)">
          <Para>
            any rights, remedies, obligations or liabilities of the parties that have accrued up to the
            date of termination, including the right to claim damages in respect of any breach of the
            agreement which existed at or before the date of termination shall not be affected or
            prejudiced.
          </Para>
        </SubClause>
      </Clause>
    </Section>

    <Section number="15" title="Force majeure">
      <Para>
        Neither party shall be in breach of this agreement nor liable for delay in performing, or failure
        to perform, any of its obligations under this agreement if such delay or failure result from
        events, circumstances or causes beyond its reasonable control. The time for performance of such
        obligations shall be extended accordingly. If the period of delay or non-performance continues for
        six months, the party not affected may terminate this agreement by giving 30 days written notice
        to the affected party.
      </Para>
    </Section>

    <Section number="16" title="Conflict">
      <Clause number="16.1">
        <Para>
          If the parties have agreed that there is one or more other agreements which may explicitly or be
          reasonably deemed to cover or affect the subject matter of the Services and as a result there is
          any conflict or inconsistency between any of the provisions of this agreement and any other
          relevant agreement between the parties, the parties will seek to resolve such conflict or
          inconsistency through the Dispute Resolution Procedure set out in clause 26.
        </Para>
      </Clause>
      <Clause number="16.2">
        <Para>
          If there is an inconsistency between any of the provisions in the main body of this agreement
          and the Schedules, the provisions in the main body of this agreement shall prevail.
        </Para>
      </Clause>
    </Section>

    <Section number="17" title="Variation">
      <Para>
        No variation of this agreement shall be effective unless it is in writing and signed by the
        parties (or their authorised representatives).
      </Para>
    </Section>

    <Section number="18" title="Waiver">
      <Clause number="18.1">
        <Para>
          A waiver of any right or remedy is only effective if given in writing and shall not be deemed a
          waiver of any subsequent right or remedy.
        </Para>
      </Clause>
      <Clause number="18.2">
        <Para>
          A delay or failure to exercise, or the single or partial exercise of, any right or remedy shall
          not waive that or any other right or remedy, nor shall it prevent or restrict the further
          exercise of that or any other right or remedy.
        </Para>
      </Clause>
    </Section>

    <Section number="19" title="Rights and remedies">
      <Para>
        Except as expressly provided in this agreement, the rights and remedies provided under this
        agreement are in addition to, and not exclusive of, any rights or remedies provided by law.
      </Para>
    </Section>

    <Section number="20" title="Severance">
      <Clause number="20.1">
        <Para>
          If any provision or part-provision of this agreement is or becomes invalid, illegal or
          unenforceable, it shall be deemed deleted, but that shall not affect the validity and
          enforceability of the rest of this agreement.
        </Para>
      </Clause>
      <Clause number="20.2">
        <Para>
          If any provision or part-provision of this agreement is deemed deleted under clause 20.1 the
          parties shall negotiate in good faith to agree a replacement provision that, to the greatest
          extent possible, achieves the intended commercial result of the original provision.
        </Para>
      </Clause>
    </Section>

    <Section number="21" title="Entire agreement">
      <Clause number="21.1">
        <Para>
          Unless otherwise expressly referred to herein or otherwise agreed in writing between the parties
          this agreement constitutes the entire agreement between the parties in relation to the Services
          and supersedes and extinguishes all previous and contemporaneous agreements, promises,
          assurances and understandings between them, whether written or oral, relating to its subject
          matter.
        </Para>
      </Clause>
      <Clause number="21.2">
        <Para>
          Each party acknowledges that in entering into this agreement it does not rely on, and shall have
          no remedies in respect of, any statement, representation, assurance or warranty (whether made
          innocently or negligently) that is not set out in this agreement.
        </Para>
      </Clause>
      <Clause number="21.3">
        <Para>
          Each party agrees that it shall have no claim for innocent or negligent misrepresentation or
          negligent misstatement based on any statement in this agreement.
        </Para>
      </Clause>
      <Clause number="21.4"><Para>Nothing in this clause shall limit or exclude any liability for fraud.</Para></Clause>
    </Section>

    <Section number="22" title="Assignment">
      <Clause number="22.1">
        <Para>
          The Client shall not, without the prior written consent of the Supplier, assign, novate,
          transfer, charge, sub-contract or deal in any other manner with all or any of its rights or
          obligations under this agreement.
        </Para>
      </Clause>
      <Clause number="22.2">
        <Para>
          The Supplier may at any time assign, transfer, charge, sub-contract or deal in any other manner
          with all or any of its rights or obligations under this agreement.
        </Para>
      </Clause>
    </Section>

    <Section number="23" title="No partnership or agency">
      <Para>
        Nothing in this agreement is intended to or shall operate to create a partnership between the
        parties, or authorise either party to act as agent for the other, and neither party shall have the
        authority to act in the name or on behalf of or otherwise to bind the other in any way (including,
        but not limited to, the making of any representation or warranty, the assumption of any obligation
        or liability and the exercise of any right or power).
      </Para>
    </Section>

    <Section number="24" title="Third party rights">
      <Para>
        This agreement does not confer any rights on any person or party (other than the parties to this
        agreement and, where applicable, their successors and permitted assigns) pursuant to the Contracts
        (Rights of Third Parties) Act 1999.
      </Para>
    </Section>

    <Section number="25" title="Notices">
      <Clause number="25.1">
        <Para>Any notice given to a party under or in connection with this agreement shall be in writing and shall be:</Para>
        <SubClause marker="(a)">
          <Para>
            delivered by hand or by pre-paid first-class post or other next working day delivery service
            at its registered office (if a company) or its principal place of business (in any other
            case); or
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>sent by email to a director or the company secretary of the party.</Para>
        </SubClause>
      </Clause>
      <Clause number="25.2">
        <Para>Any notice shall be deemed to have been received:</Para>
        <SubClause marker="(a)">
          <Para>if delivered by hand, at the time the notice is left at the proper address;</Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            if sent by pre-paid first-class post or other next working day delivery service, at 9.00 am on
            the second Business Day after posting; or
          </Para>
        </SubClause>
        <SubClause marker="(c)">
          <Para>
            if sent by email, at the time of transmission, or, if this time falls outside Business Hours
            in the place of receipt, when Business Hours resume.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="25.3">
        <Para>
          This clause does not apply to the service of any proceedings or other documents in any legal
          action or, where applicable, any arbitration or other method of dispute resolution.
        </Para>
      </Clause>
    </Section>

    <Section number="26" title="Disputes">
      <Clause number="26.1">
        <Para>
          If any dispute arises in connection with this agreement or the performance, validity or
          enforceability of it the parties shall adopt the following procedure:
        </Para>
        <SubClause marker="(a)">
          <Para>
            a director or other senior representatives of the parties will within 14 days of a written
            request from one party to the other meet in a good faith effort to resolve the dispute.
          </Para>
        </SubClause>
        <SubClause marker="(b)">
          <Para>
            If the dispute is not wholly resolved at that meeting the parties agree to enter into
            mediation in good faith to settle such a dispute in accordance with the CEDR Model Mediation
            Procedure.
          </Para>
        </SubClause>
      </Clause>
      <Clause number="26.2">
        <Para>
          No party may commence any court proceedings/arbitration in relation to any dispute arising out
          of this agreement until it has attempted to settle the dispute by mediation and either the
          mediation has terminated all the other party has failed to participate in the mediation
          provided that the right to issue proceedings is not prejudiced by a delay.
        </Para>
      </Clause>
    </Section>

    <Section number="27" title="Governing law">
      <Para>
        This agreement and any dispute or claim arising out of or in connection with it or its subject
        matter or formation (including non-contractual disputes or claims) shall be governed by and
        interpreted in accordance with the law of England and Wales.
      </Para>
    </Section>

    <Section number="28" title="Jurisdiction">
      <Para>
        Each party irrevocably agrees that the courts of England and Wales shall have exclusive
        jurisdiction to settle any dispute or claim arising out of or in connection with this agreement
        or its subject matter or formation (including non-contractual disputes or claims).
      </Para>
    </Section>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>
        Schedule 1 — Authorised Users and Subscription Fees
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2 }}>
        1. Authorised Users
      </Typography>
      <Typography variant="body2" paragraph>
        The Authorised Users shall be a maximum of 4 per User Subscription at any one time unless
        otherwise agreed in writing under the terms of this agreement and whose names shall be notified in
        writing to the Supplier from time to time and whenever reasonably requested by the Supplier.
      </Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>2. Subscription Fees</Typography>
      <Typography variant="body2" paragraph>As per described in the above proposal.</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>3. Additional User Subscription Fees</Typography>
      <Typography variant="body2" paragraph>As per described in the above proposal.</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>4. Support Fees</Typography>
      <Typography variant="body2" paragraph>As per described in the above proposal.</Typography>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Schedule 2 — Subscription Term</Typography>
      <Typography variant="body2" paragraph>As per described in the above proposal.</Typography>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Schedule 3 — Mandatory Policies</Typography>
      <Typography variant="body2" paragraph>The Mandatory Policies are:</Typography>
      <Box component="ul" sx={{ pl: 3, mt: 0 }}>
        <Typography component="li" variant="body2">Data and Privacy Policy</Typography>
        <Typography component="li" variant="body2">
          Cookies Policy:
          <Box component="ul" sx={{ pl: 3 }}>
            <Typography component="li" variant="body2">ARRAffinity and ARRAffinitySameSite cookies</Typography>
            <Typography component="li" variant="body2">orchantiforgery_4ch1hqnzb63fkrkactfqgg155p</Typography>
            <Typography component="li" variant="body2">admin_Default</Typography>
            <Typography component="li" variant="body2">ai_user</Typography>
            <Typography component="li" variant="body2">orchauth_Default</Typography>
            <Typography component="li" variant="body2">Google Analytics</Typography>
          </Box>
        </Typography>
      </Box>
      <Typography variant="body2" paragraph>and any other policies notified by the Supplier from time to time.</Typography>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Schedule 4 — Services</Typography>
      <Typography variant="body2" paragraph>As per described in the above proposal.</Typography>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" gutterBottom>Schedule 5 — Support Services</Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(180px, 1fr) 2fr',
          border: (t) => `1px solid ${t.palette.divider}`,
          '& > div': {
            p: 1.5,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
          },
          '& > div:nth-of-type(2n)': {
            borderLeft: (t) => `1px solid ${t.palette.divider}`,
          },
        }}
      >
        <Box sx={{ fontWeight: 600 }}>Support</Box>
        <Box sx={{ fontWeight: 600 }}>Description</Box>

        <Box>Technical support for app availability issues.</Box>
        <Box>
          Assistance provided to Backfill Pro users if they encounter difficulties accessing the Backfill
          Pro platform due to server issues or connectivity problems.
        </Box>

        <Box>Technical support for bugs</Box>
        <Box>
          Help provided to address any software glitches or malfunctions within the Backfill Pro platform
          that hinder its proper functioning.
        </Box>

        <Box>Training and app use support.</Box>
        <Box>
          Guidance offered to Backfill Pro users on how to effectively utilize the Backfill Pro platform
          for planning, scheduling, and recipe optimization in backfill operations.
        </Box>

        <Box>Data updates to reticulation and rheology</Box>
        <Box>
          Assistance with updating and maintaining accurate data related to the reticulation (distribution
          network) and rheology (flow properties) within the Backfill Pro platform, ensuring the
          reliability of planning and scheduling algorithms.
        </Box>

        <Box>Application Maintenance</Box>
        <Box>
          Ongoing maintenance tasks performed to keep the Backfill Pro platform running smoothly,
          including software updates, patches, and system optimizations.
        </Box>

        <Box>Application performance (response times)</Box>
        <Box>
          Monitoring and optimization of the Backfill Pro platform&apos;s performance to ensure fast
          response times, minimizing delays in accessing data and performing tasks.
        </Box>

        <Box>Incidents (unplanned service disruptions)</Box>
        <Box>
          Rapid response and resolution of any unforeseen issues or disruptions to Backfill Pro services,
          ensuring minimal downtime for mining operations.
        </Box>

        <Box>Application and data security</Box>
        <Box>
          Implementation and maintenance of robust security measures to safeguard sensitive data and
          prevent unauthorized access or breaches within the Backfill Pro platform.
        </Box>

        <Box sx={{ borderBottom: 'none !important' }}>Data Backups</Box>
        <Box sx={{ borderBottom: 'none !important' }}>
          Regular backups of critical data stored within the Backfill Pro platform to prevent loss in
          case of system failures or data corruption, ensuring data integrity and continuity of
          operations.
        </Box>
      </Box>
    </Box>

    {showAcceptConfirmation && (
      <Typography variant="body2" paragraph sx={{ fontStyle: 'italic', mt: 4 }}>
        By clicking &apos;Accept&apos; you confirm that you have read, understood and agreed to these terms
        and conditions.
      </Typography>
    )}
  </Box>
);

export default TermsAndConditionsContent;
